import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND_LOGO } from "../data/brand";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/films", label: "Projects" },
  { to: "/in-production", label: "In Production" },
  { to: "/on-the-floor", label: "On the Floor" },
  { to: "/team", label: "Team" },
  { to: "/gallery", label: "Gallery" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-[100] transition-[background,backdrop-filter,border-color] duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/75 border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 md:px-10 py-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <img
            src={BRAND_LOGO}
            alt="Dhara Kala Movies"
            className="w-11 h-11 md:w-12 md:h-12 object-contain"
            style={{ filter: "brightness(0) saturate(100%) invert(69%) sepia(64%) saturate(431%) hue-rotate(2deg) brightness(94%) contrast(89%)" }}
          />
          <span className="font-serif-display leading-none hidden sm:inline-block">
            <span className="block text-[9px] tracking-overline uppercase text-[color:var(--dk-gold)]">Dhara Kala</span>
            <span className="block text-white text-lg -mt-0.5">Movies</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.replace(/\s+/g, "-").toLowerCase()}-link`}
              className={({ isActive }) =>
                `text-[10px] uppercase tracking-overline font-medium eyebrow-link transition-colors ${
                  isActive ? "text-[color:var(--dk-gold)]" : "text-white/70 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden text-white p-2 -mr-2"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="xl:hidden bg-black/95 backdrop-blur-xl border-b border-white/10 max-h-[85vh] overflow-y-auto"
            data-testid="nav-mobile-panel"
          >
            <div className="px-6 py-6 flex flex-col gap-3">
              {links.map((l, i) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-mobile-${l.label.replace(/\s+/g, "-").toLowerCase()}-link`}
                  className={({ isActive }) =>
                    `font-serif-display text-2xl transition-colors ${
                      isActive ? "text-[color:var(--dk-gold)]" : "text-white"
                    }`
                  }
                >
                  <span className="text-[color:var(--dk-gold)]/60 text-xs align-top mr-3">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
